/**
 * VertexLabPage.jsx
 * Experiencia de simulación autenticada con la estética del Simulador Público.
 *
 * Ruta: /vertex/lab  (sin DashboardLayout — pantalla completa)
 *
 * Flujo de fases:
 *   loading → selection → onboarding → confirming → simulation → complete → evaluation
 *
 * Inspirado visualmente en SimuladorB2B.jsx + PublicTrialSimulator.jsx
 * Motor: V4 autenticado (misma lógica que VertexSimulator.jsx)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeft, Send, Loader2, ChevronDown, ChevronUp,
  MessageSquare, Users, Target, BarChart3, ArrowRight,
  AlertTriangle, RefreshCw, X, CheckCircle2, Brain,
  Sparkles, Menu, Shield, Volume2
} from 'lucide-react';
import { getSafeToken } from '../../utils/tokenHelper';
import SidebarAsistentes from '../../components/SidebarAsistentes';
import { VERTEX_API_BASE } from '../../utils/vertexApiHelper';
import safeFetchJson from '../../utils/safeFetchJson';
import EvaluationReport from './components/EvaluationReport';
import VoiceInputUX from '../../components/VoiceInputUX';
import { textToSpeech } from '../../services/voiceApiService';

// ── Constantes ─────────────────────────────────────────────────────────────
const MAX_INPUT_CHARS = 400;

// ── Utilidades ─────────────────────────────────────────────────────────────
function sanitizeInput(raw) {
  return raw
    .replace(/<[^>]*>/g, '')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .slice(0, MAX_INPUT_CHARS);
}

/** Genera un id único para cada mensaje local */
let _msgCounter = 0;
function msgId() { return `msg-${++_msgCounter}-${Date.now()}`; }

/**
 * Extrae el texto del contexto/briefing que llega del backend.
 * Maneja múltiples formatos posibles.
 */
function extractBriefingText(briefing) {
  if (!briefing) return '';
  // Estructura V4 estricta
  if (briefing?.payload?.escenario?.texto) return briefing.payload.escenario.texto;
  // Content string
  if (typeof briefing?.content === 'string') return briefing.content;
  // Texto plano directo
  if (typeof briefing === 'string') return briefing;
  return '';
}

function extractBriefingActor(briefing) {
  if (!briefing?.payload?.actor) return null;
  return briefing.payload.actor;
}

/**
 * Renderiza texto con markdown básico y jerarquía visual.
 * Igual que PublicTrialSimulator.renderContextText
 */
function ContextText({ text }) {
  if (!text) return null;

  const qMatch = text.match(/(¿[^?]+\?)/);
  const question = qMatch ? qMatch[1] : null;
  const beforeQ = question
    ? text.slice(0, text.indexOf(question)).trim()
    : text.trim();
  const afterQ = question
    ? text.slice(text.indexOf(question) + question.length).trim().replace(/^\.?\s*/, '')
    : '';
  const sentences = beforeQ.split(/\.\s+/).map(s => s.trim()).filter(s => s.length > 5);
  const retaSentence = sentences.length > 1 ? sentences[sentences.length - 1] : null;
  const contextSentences = retaSentence ? sentences.slice(0, -1) : sentences;

  return (
    <div className="space-y-3">
      {contextSentences.map((s, i) => (
        <p key={i} className="text-[13px] text-slate-500 leading-relaxed">{s}.</p>
      ))}
      {retaSentence && (
        <p className="text-[13px] font-semibold text-slate-700 leading-snug pl-3 border-l-2 border-[#1E3A8A]">
          {retaSentence}.
        </p>
      )}
      {question && (
        <div className="rounded-xl px-4 py-3 mt-1"
          style={{ background: 'rgba(30,58,138,0.07)', border: '1px solid rgba(30,58,138,0.15)' }}>
          <p className="text-[14px] font-bold text-[#1E3A8A] leading-snug">{question}</p>
          {afterQ && <p className="text-[11px] text-slate-400 italic mt-1.5">{afterQ}</p>}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
export default function VertexLabPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // ── Estado de fases ────────────────────────────────────────────────────────
  // loading | selection | onboarding | confirming | simulation | complete | evaluation
  const [phase, setPhase] = useState('loading');

  // ── Estado de agentes ──────────────────────────────────────────────────────
  const [agents, setAgents] = useState([]);
  const [loadError, setLoadError] = useState('');

  // ── Estado de sesión activa ────────────────────────────────────────────────
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [onboardingData, setOnboardingData] = useState(null); // { title, message, consent }
  const [briefingText, setBriefingText] = useState('');
  const [briefingActor, setBriefingActor] = useState(null);
  const [completedTurns, setCompletedTurns] = useState(0);
  const [maxTurns, setMaxTurns] = useState(8);
  const [sessionStatus, setSessionStatus] = useState('active');

  // ── Estado de mensajes ────────────────────────────────────────────────────
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [moderationAlert, setModerationAlert] = useState('');
  const moderationTimerRef = useRef(null);

  // ── Evaluación ────────────────────────────────────────────────────────────
  const [isGeneratingEval, setIsGeneratingEval] = useState(false);
  const [evaluationSessionId, setEvaluationSessionId] = useState(null);

  // ── UI / Mobile ───────────────────────────────────────────────────────────
  const [sheetOpen, setSheetOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [contextViewed, setContextViewed] = useState(false);
  const [showExitSheet, setShowExitSheet] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  // ── Audio: state ───────────────────────────────────────────────────────────
  const [isTTSEnabled, setIsTTSEnabled] = useState(false);
  const [showAudioUnlockPrompt, setShowAudioUnlockPrompt] = useState(false);
  const [assistantAudioState, setAssistantAudioState] = useState('idle'); // 'idle' | 'playing'

  // ── Refs ──────────────────────────────────────────────────────────────────
  const chatBodyRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const hasAutoStarted = useRef(false);

  // ── Audio: refs ────────────────────────────────────────────────────────────
  const audioEngineRef = useRef(null);         // Elemento <audio> persistente
  const audioUnlockedRef = useRef(false);      // Flag autoplay desbloqueado
  const unlockInProgressRef = useRef(false);   // Previene unlocks concurrentes
  const audioCache = useRef({});               // { contentHash: { audioBase64, contentType } }
  const audioQueue = useRef([]);               // Cola: [{ cacheKey, aId }]
  const isProcessingQueueRef = useRef(false);
  const hasShownAudioModalRef = useRef(false);

  // ── Control de acceso ─────────────────────────────────────────────────────
  useEffect(() => {
    const isAdmin = user?.email === 'davidprudencio@hotmail.com';
    const hasAgents = user?.assignedAgents?.length > 0;
    if (user && !isAdmin && !hasAgents) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // ── Carga de agentes ──────────────────────────────────────────────────────
  useEffect(() => {
    const isAdmin = user?.email === 'davidprudencio@hotmail.com';
    const hasAgents = user?.assignedAgents?.length > 0;
    if (isAdmin || hasAgents) fetchAgents();
  }, [user]);

  async function fetchAgents() {
    setPhase('loading');
    const token = getSafeToken();
    if (!token) { setLoadError('Sesión expirada. Inicia sesión de nuevo.'); setPhase('selection'); return; }

    const result = await safeFetchJson(`${VERTEX_API_BASE}/v4/agents`, { method: 'GET', token, verbose: false });
    if (!result.ok) {
      setLoadError(result.error || 'Error al cargar agentes.');
      setPhase('selection');
      return;
    }
    const data = result.data;
    if (data.success) {
      setAgents(data.agents || []);
      setPhase('selection');
    } else {
      setLoadError(data.message || 'Error al cargar agentes.');
      setPhase('selection');
    }
  }

  // Auto-start desde query param (deep link desde cursos)
  useEffect(() => {
    if (phase !== 'selection' || hasAutoStarted.current) return;
    const agentIdFromQuery = searchParams.get('agentId') || searchParams.get('agent');
    if (!agentIdFromQuery || agents.length === 0) return;
    const target = agents.find(a => a.agentId === agentIdFromQuery);
    if (target) { hasAutoStarted.current = true; handleSelectAgent(target); }
  }, [agents, phase, searchParams]);

  // ── Scroll messages ───────────────────────────────────────────────────────
  useEffect(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (nearBottom) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  // Auto-resize textarea
  function autoResize() {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }

  // ── Iniciar sesión para un agente ─────────────────────────────────────────
  async function handleSelectAgent(agent) {
    setSelectedAgent(agent);
    setPhase('confirming'); // Spinner mientras hace el start
    setLoadError('');

    const token = getSafeToken();
    if (!token) { setLoadError('Sesión expirada.'); setPhase('selection'); return; }

    try {
      // Obtener detalles completos si es V4
      const isV4 = agent.coachConfig?.enabled;
      let resolvedAgent = agent;
      if (isV4) {
        try {
          const dr = await fetch(`${VERTEX_API_BASE}/v4/agents/${agent.agentId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const dd = await dr.json();
          if (dd?.success && dd?.agent) resolvedAgent = dd.agent;
        } catch { /* usa agent básico */ }
      }
      setSelectedAgent(resolvedAgent);

      const endpoint = isV4
        ? `${VERTEX_API_BASE}/v4/session/start`
        : `${VERTEX_API_BASE}/chat/start`;

      // Calcular primer nombre del usuario
      const rawName = user?.nombre || user?.name || user?.email?.split('@')[0] || '';
      const firstName = rawName.trim().split(/\s+/)[0].split(/[._-]/)[0];
      const userName = firstName
        ? firstName.charAt(0).toUpperCase() + firstName.slice(1)
        : '';

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          agentId: resolvedAgent.agentId,
          userName,
          ...(isV4 && resolvedAgent.scenarioTemplates?.[0] && {
            scenarioId: resolvedAgent.scenarioTemplates[0].id
          })
        })
      });

      const data = await res.json();

      if (isV4) {
        if (!data.sessionId || !data.onboarding) {
          setLoadError(data.error || 'Error al iniciar la simulación.');
          setPhase('selection');
          return;
        }
        setSessionId(data.sessionId);
        setOnboardingData(data.onboarding);
        setMaxTurns(data.maxTurns || 8);
        setCompletedTurns(data.completedTurns ?? 0);
        setPhase('onboarding');
      } else {
        if (!data.success) { setLoadError(data.message || 'Error.'); setPhase('selection'); return; }
        setSessionId(data.sessionId);
        setMessages([{
          id: msgId(), role: 'agent',
          content: `Hola, soy ${data.agentInfo?.nombreVisible}. ${data.agentInfo?.descripcion || ''}`
        }]);
        setPhase('simulation');
      }
    } catch (err) {
      console.error('[VertexLabPage] Error en handleSelectAgent:', err);
      setLoadError('Error al conectar. Intenta de nuevo.');
      setPhase('selection');
    }
  }

  // ── Confirmar onboarding ──────────────────────────────────────────────────
  async function handleConfirmOnboarding() {
    if (!sessionId) return;
    setPhase('confirming');

    const token = getSafeToken();
    try {
      const res = await fetch(`${VERTEX_API_BASE}/v4/session/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ sessionId })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error al confirmar sesión');

      // Extraer briefing
      const bText = extractBriefingText(data.briefing);
      const bActor = extractBriefingActor(data.briefing);
      setBriefingText(bText);
      setBriefingActor(bActor);

      // Extraer kickoff (primer mensaje del agente)
      const initialMsgs = [];
      const msgs = Array.isArray(data.messages) ? data.messages : [];
      const kickoff = data.kickoff || msgs.find(m => m?.metadata?.type === 'SYSTEM_KICKOFF');
      let kickoffMsgId = null;
      if (kickoff) {
        const content = typeof kickoff.content === 'string' ? kickoff.content
          : typeof kickoff.text === 'string' ? kickoff.text : '';
        if (content.trim()) {
          kickoffMsgId = msgId();
          initialMsgs.push({ id: kickoffMsgId, role: 'agent', content: content.trim() });
        }
      }
      // Mensajes adicionales: solo turnos reales USER ↔ AGENT
      // El brief (tipo_visual=briefing / SYSTEM_BRIEF / isBrief) va al panel izquierdo, no al chat
      // El kickoff ya se extrajo arriba — también se salta
      msgs.forEach(m => {
        if (m?.metadata?.type === 'SYSTEM_KICKOFF') return;
        if (m?.metadata?.type === 'SYSTEM_BRIEF') return;
        if (m?.metadata?.isBrief === true) return;
        if (m?.tipo_visual === 'briefing') return;
        if (m?.messageType === 'briefing') return;
        // Solo procesar mensajes de conversación real (user / model / assistant sin pre-roll)
        if (m?.role !== 'user' && m?.role !== 'model' && m?.role !== 'assistant') return;
        const content = m.content || m.text || '';
        if (!content.trim()) return;
        const role = m.role === 'user' ? 'user' : 'agent';
        initialMsgs.push({ id: msgId(), role, content: content.trim() });
      });

      setMessages(initialMsgs);
      setPhase('simulation');
      // Reproducir kickoff con TTS
      if (kickoffMsgId) {
        const kickoffContent = initialMsgs.find(m => m.id === kickoffMsgId)?.content;
        if (kickoffContent) generateAndPlayAudio(kickoffContent, kickoffMsgId);
      }
    } catch (err) {
      console.error('[VertexLabPage] Error confirmando:', err);
      setLoadError('Error al iniciar simulación. Intenta de nuevo.');
      setPhase('onboarding');
    }
  }

  // ── Cancelar/Salir ────────────────────────────────────────────────────────
  function handleCancelOnboarding() {
    hasAutoStarted.current = true; // Bloquear auto-start
    setSelectedAgent(null);
    setSessionId(null);
    setOnboardingData(null);
    setPhase('selection');
  }

  function handleExitSimulation() {
    hasAutoStarted.current = true;
    // Detener audio activo
    if (audioEngineRef.current) {
      try { audioEngineRef.current.pause(); audioEngineRef.current.src = ''; } catch {}
    }
    setAssistantAudioState('idle');
    setSelectedAgent(null);
    setSessionId(null);
    setMessages([]);
    setBriefingText('');
    setBriefingActor(null);
    setPhase('selection');
  }

  // ── Enviar mensaje ────────────────────────────────────────────────────────
  async function sendMessage() {
    const text = sanitizeInput(input).trim();
    if (!text || text.length < 2 || sending || !sessionId || phase !== 'simulation') return;

    const userMsg = { id: msgId(), role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setSending(true);
    setModerationAlert('');

    const token = getSafeToken();
    try {
      const res = await fetch(`${VERTEX_API_BASE}/v4/session/turn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ sessionId, message: text })
      });
      const data = await res.json();

      // Sesión bloqueada / cerrada por el servidor
      if (data.sessionClosed || data.status === 'armed_to_close') {
        setPhase('complete');
        return;
      }

      // Mensaje bloqueado por moderador
      if (data.status === 'blocked') {
        setMessages(prev => prev.filter(m => m.id !== userMsg.id));
        setInput(text);
        const msg = data.message || data.moderatorAlert?.message || 'Ese mensaje no puede enviarse. Reformúlalo.';
        setModerationAlert(msg);
        clearTimeout(moderationTimerRef.current);
        moderationTimerRef.current = setTimeout(() => setModerationAlert(''), 6000);
        return;
      }

      if (!data.success && !data.content && !data.response) {
        throw new Error(data.message || data.error || 'Error al procesar respuesta.');
      }

      // Alerta de moderación no bloqueante
      const modAlert = data.moderatorAlert;
      if (modAlert?.active && modAlert?.message) {
        setModerationAlert(modAlert.message);
        clearTimeout(moderationTimerRef.current);
        moderationTimerRef.current = setTimeout(() => setModerationAlert(''), 6000);
      }

      // Respuesta del agente
      const aiText = data.content || data.response || data.messages?.[0]?.content || '';
      if (aiText.trim()) {
        const nid = msgId();
        setMessages(prev => [...prev, { id: nid, role: 'agent', content: aiText.trim() }]);
        generateAndPlayAudio(aiText.trim(), nid);
      }

      // Actualizar contadores
      const newTurns = data.counters?.completedTurns ?? data.completedTurns ?? completedTurns + 1;
      setCompletedTurns(newTurns);
      setSessionStatus(data.sessionStatus || 'active');

      if (data.isComplete || data.sessionStatus === 'completed' || newTurns >= maxTurns) {
        setPhase('complete');
      }

    } catch (err) {
      console.error('[VertexLabPage] Error en turno:', err);
      setMessages(prev => [...prev, {
        id: msgId(), role: 'system',
        content: 'Error al procesar tu mensaje. Intenta de nuevo.'
      }]);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e) {
    const isMobile = window.innerWidth < 1024;
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
      e.preventDefault();
      sendMessage();
    }
  }

  function handleInputFocus() {
    setSheetOpen(false);
    setTimeout(() => {
      chatBodyRef.current?.scrollTo({ top: chatBodyRef.current.scrollHeight, behavior: 'smooth' });
    }, 250);
  }

  // ── Audio: inicializar engine persistente ─────────────────────────────────
  useEffect(() => {
    if (!audioEngineRef.current) {
      const el = new Audio();
      el.playsInline = true;
      el.preload = 'auto';
      audioEngineRef.current = el;
    }
  }, []);

  // Reset audio al cambiar de agente/sesión
  useEffect(() => {
    hasShownAudioModalRef.current = false;
    audioUnlockedRef.current = false;
    audioQueue.current = [];
    isProcessingQueueRef.current = false;
    sessionStorage.removeItem('audioModalShown');
  }, [selectedAgent?.agentId]);

  // ── unlockAudio: desbloquea autoplay del browser ──────────────────────────
  function unlockAudio() {
    return new Promise((resolve, reject) => {
      if (audioUnlockedRef.current) { resolve(true); return; }
      if (unlockInProgressRef.current) { resolve(false); return; }
      unlockInProgressRef.current = true;

      const hasIOSUA = /iPhone|iPad|iPod/.test(navigator.userAgent);
      const hasIOSPlatform = /iPhone|iPad|iPod|Macintosh/.test(navigator.platform) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isiOS = hasIOSUA || hasIOSPlatform;

      const silentSrc = isiOS
        ? 'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMD/////////////////////////////////////////////////////////////////AAAAAExhdmM1OC4xMzQAAAAAAAAAAAAAAAAkAiQAAAAAAAABhEgq4G8AAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQZAAAAANIAAAAAExBTUUzLjk5LjVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV'
        : 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

      const silentAudio = new Audio(silentSrc);
      silentAudio.playsInline = true;
      silentAudio.muted = true;
      silentAudio.preload = 'auto';

      let handled = false;
      const done = (success) => {
        if (handled) return;
        handled = true;
        clearTimeout(timeoutId);
        unlockInProgressRef.current = false;
        if (success) { audioUnlockedRef.current = true; resolve(true); }
        else reject(new Error('unlock failed'));
      };

      const timeoutId = setTimeout(() => {
        audioUnlockedRef.current = true;
        done(true);
      }, 2000);

      silentAudio.addEventListener('playing', () => done(true), { once: true });
      silentAudio.addEventListener('error', () => done(false), { once: true });
      silentAudio.play().catch(err => {
        if (err.name === 'AbortError' || err.name === 'NotSupportedError') {
          audioUnlockedRef.current = true; done(true);
        } else { done(false); }
      });
    });
  }

  // ── processAudioQueue: reproduce cola pendiente ───────────────────────────
  async function processAudioQueue() {
    if (isProcessingQueueRef.current || audioQueue.current.length === 0) return;
    isProcessingQueueRef.current = true;
    while (audioQueue.current.length > 0) {
      const { cacheKey, aId } = audioQueue.current[0];
      try { await playAudioFromCache(cacheKey, aId); } catch {}
      audioQueue.current.shift();
    }
    isProcessingQueueRef.current = false;
  }

  // ── playAudioFromCache: reproduce audio desde cache ───────────────────────
  function playAudioFromCache(cacheKey, displayId) {
    return new Promise((resolve) => {
      const cached = audioCache.current[cacheKey];
      if (!cached) { resolve(); return; }

      if (!audioUnlockedRef.current) {
        const alreadyQueued = audioQueue.current.some(i => i.aId === displayId);
        if (!alreadyQueued) audioQueue.current.push({ cacheKey, aId: displayId });
        if (!hasShownAudioModalRef.current) {
          setShowAudioUnlockPrompt(true);
          hasShownAudioModalRef.current = true;
          sessionStorage.setItem('audioModalShown', Date.now().toString());
        }
        resolve();
        return;
      }

      const engine = audioEngineRef.current;
      if (!engine) { resolve(); return; }

      const hasIOSUA = /iPhone|iPad|iPod/.test(navigator.userAgent);
      const hasIOSPlatform = /iPhone|iPad|iPod|Macintosh/.test(navigator.platform) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isiOS = hasIOSUA || hasIOSPlatform;
      const isSafariIOS = isiOS && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|OPiOS/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      const useDataURL = isSafariIOS || isAndroid;

      let normalizedType = cached.contentType || 'audio/mpeg';
      if (normalizedType === 'audio/mp3') normalizedType = 'audio/mpeg';

      let audioUrl;
      if (useDataURL) {
        audioUrl = `data:${normalizedType};base64,${cached.audioBase64}`;
      } else {
        try {
          const bytes = atob(cached.audioBase64);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          audioUrl = URL.createObjectURL(new Blob([arr], { type: normalizedType }));
        } catch {
          audioUrl = `data:${normalizedType};base64,${cached.audioBase64}`;
        }
      }

      if (!engine.paused) { engine.pause(); }
      engine.src = audioUrl;
      engine.playsInline = true;
      engine.muted = false;
      engine.volume = 1.0;
      engine.preload = 'auto';
      engine.load();

      engine.onended = () => { setAssistantAudioState('idle'); resolve(); };
      engine.onerror = () => { setAssistantAudioState('idle'); resolve(); };
      engine.onplaying = () => { setAssistantAudioState('playing'); };

      engine.play().catch(() => resolve());
    });
  }

  // ── generateAndPlayAudio: genera TTS y reproduce ──────────────────────────
  async function generateAndPlayAudio(text, id) {
    if (!isTTSEnabled || !text?.trim() || !id) return;
    const contentHash = `hash-${text.slice(0, 50).replace(/\s+/g, '-')}-${text.length}`;
    if (audioCache.current[contentHash]) {
      playAudioFromCache(contentHash, id);
      return;
    }
    try {
      const agentId = selectedAgent?.agentId || 'default';
      const response = await textToSpeech({ text, agentId, voiceRole: 'client' });
      audioCache.current[contentHash] = { audioBase64: response.audioBase64, contentType: response.contentType };
      await playAudioFromCache(contentHash, id);
    } catch (err) {
      console.warn('[VertexLabPage] TTS error:', err?.message);
    }
  }

  // ── Generar / ver evaluación ──────────────────────────────────────────────
  async function handleGenerateEvaluation() {
    if (!sessionId) return;
    setIsGeneratingEval(true);
    const token = getSafeToken();
    try {
      const res = await fetch(`${VERTEX_API_BASE}/v4/session/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ sessionId })
      });
      const data = await res.json();
      if (!data.success) { alert('Error al generar evaluación: ' + (data.error || 'Desconocido')); return; }
      setEvaluationSessionId(sessionId);
      setPhase('evaluation');
    } catch (err) {
      console.error('[VertexLabPage] Error evaluación:', err);
      alert('Error al generar evaluación.');
    } finally {
      setIsGeneratingEval(false);
    }
  }

  function handleCloseEvaluation() {
    setPhase('selection');
    setSelectedAgent(null);
    setSessionId(null);
    setMessages([]);
    setBriefingText('');
    setEvaluationSessionId(null);
    hasAutoStarted.current = false;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: EVALUACIÓN
  // ══════════════════════════════════════════════════════════════════════════
  if (phase === 'evaluation' && evaluationSessionId) {
    return <EvaluationReport sessionId={evaluationSessionId} onClose={handleCloseEvaluation} />;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // SHARED: APP BAR
  // ══════════════════════════════════════════════════════════════════════════
  const AppBar = ({ onBack, title, subtitle, onMenu }) => (
    <div
      className="bg-[#081827] flex items-center gap-3 px-4 flex-shrink-0 z-20 relative"
      style={{ height: '52px' }}
    >
      <button
        onClick={onBack}
        className="p-1.5 -ml-1 text-gray-400 hover:text-white transition-colors"
        aria-label="Atrás"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>
      <img
        src="/isotipoedutech.png"
        alt="Edutech Pulse Lab"
        className="w-8 h-8 rounded-full flex-shrink-0 ring-1 ring-white/20"
        onError={e => { e.target.style.display = 'none'; }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold text-sm leading-tight truncate">{title}</p>
        {subtitle && <p className="text-gray-500 text-[11px] leading-tight truncate">{subtitle}</p>}
      </div>
      {onMenu && (
        <button
          onClick={onMenu}
          className="p-1.5 -mr-1 text-gray-400 hover:text-white transition-colors"
          aria-label="Más información"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: LOADING
  // ══════════════════════════════════════════════════════════════════════════
  if (phase === 'loading' || phase === 'confirming') {
    const loadingMsg = phase === 'confirming' ? 'Preparando simulación...' : 'Cargando agentes...';
    return (
      <div className="fixed inset-0 flex flex-col bg-[#081827]">
        <AppBar
          onBack={() => navigate(phase === 'confirming' ? '/vertex/lab' : '/dashboard')}
          title="Laboratorio IA"
          subtitle="Vertex Quick"
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            <p className="text-white/60 text-sm">{loadingMsg}</p>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: SELECTION — Grid de agentes
  // ══════════════════════════════════════════════════════════════════════════
  if (phase === 'selection') {
    return (
      <>
      <div className="fixed inset-0 flex flex-col bg-white">
        <AppBar
          onBack={() => navigate('/home')}
          title="Laboratorio de Simulaciones"
          subtitle="Vertex Quick · IA Coach"
          onMenu={() => setSidebarVisible(true)}
        />

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-[#EEF2F7]">
          <div className="max-w-4xl mx-auto px-4 py-8">

            {/* Hero */}
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0c203b]/8 rounded-full mb-4">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-xs font-semibold text-[#0c203b] uppercase tracking-widest">
                  Motor V4 · Gemini + RAG
                </span>
              </div>
              <h1 className="text-2xl font-bold text-[#0c203b] leading-tight mb-2">
                Elige tu simulación
              </h1>
              <p className="text-slate-500 text-sm max-w-md mx-auto leading-relaxed">
                Escenarios conversacionales con IA diseñados para entrenar habilidades ejecutivas bajo presión real.
              </p>
            </div>

            {/* Error */}
            {loadError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-800">{loadError}</p>
                  <button onClick={fetchAgents} className="text-xs text-red-600 hover:underline mt-1 flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Reintentar
                  </button>
                </div>
              </div>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {agents.map(agent => {
                const isV4 = agent.coachConfig?.enabled;
                return (
                  <button
                    key={agent._id || agent.agentId}
                    onClick={() => handleSelectAgent(agent)}
                    className="text-left bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-lg hover:border-[#1E3A8A]/30 hover:-translate-y-1 transition-all duration-300 group flex flex-col gap-4"
                  >
                    {/* Avatar + badges */}
                    <div className="flex items-start justify-between">
                      <div className="w-14 h-14 rounded-2xl overflow-hidden border-2 border-white ring-1 ring-slate-100 shadow-sm bg-slate-50 flex-shrink-0">
                        {agent.avatar?.startsWith('/') ? (
                          <img src={agent.avatar} alt={agent.nombreVisible}
                            className="w-full h-full object-cover object-top" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-2xl">
                            {agent.avatar || '🤖'}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500">
                          <Brain className="w-2.5 h-2.5" /> Vertex
                        </span>
                        {isV4 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700">
                            <Sparkles className="w-2.5 h-2.5" /> Coach V4
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-800 text-[15px] leading-snug mb-2 group-hover:text-[#1E3A8A] transition-colors line-clamp-2">
                        {agent.nombreVisible}
                      </h3>
                      <p className="text-[12px] text-slate-500 leading-relaxed line-clamp-3">
                        {agent.descripcion}
                      </p>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-400 font-medium">
                        <Target className="w-3 h-3" /> Simulación
                      </div>
                      <span className="flex items-center gap-1 text-[12px] font-bold text-[#1E3A8A] group-hover:gap-2 transition-all">
                        Empezar
                        <ArrowRight className="w-3.5 h-3.5 transform group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Empty */}
            {agents.length === 0 && !loadError && (
              <div className="text-center py-20">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No hay simulaciones disponibles para tu cuenta.</p>
              </div>
            )}

            {/* Acceso rápido a métricas */}
            <div className="mt-10 flex justify-center">
              <button
                onClick={() => navigate('/vertex/analytics')}
                className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#0c203b] transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                Ver historial y métricas
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Nav sidebar — fixed wrapper keeps it out of flex layout */}
      <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 60, display: sidebarVisible ? 'block' : 'none' }}>
        <SidebarAsistentes
          userName={user?.nombre || user?.name || ''}
          userEmail={user?.email || ''}
          allUserChats={{}}
          handleSelectAssistantForNewChat={() => {}}
          handleSelectChat={() => {}}
          handleDeleteChat={() => {}}
          handleRenameChat={() => {}}
          selectedAssistant={null}
          selectedChatId={null}
          expanded={false}
          setExpanded={() => {}}
          sidebarVisible={sidebarVisible}
          setSidebarVisible={setSidebarVisible}
          isCreatingNewChat={false}
          MAX_CHATS_PER_ASSISTANT={10}
          loading={false}
          isAuthenticated={true}
          onBackToDashboard={() => navigate('/dashboard')}
        />
      </div>
      {sidebarVisible && (
        <div
          className="fixed inset-0 bg-black/40 z-[59] cursor-pointer"
          onClick={() => setSidebarVisible(false)}
        />
      )}
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: ONBOARDING — Pantalla completa inmersiva
  // ══════════════════════════════════════════════════════════════════════════
  if (phase === 'onboarding' && selectedAgent && onboardingData) {
    const agentAvatarEl = selectedAgent.avatar?.startsWith('/') ? (
      <img src={selectedAgent.avatar} alt={selectedAgent.nombreVisible}
        className="w-full h-full object-cover object-top" />
    ) : (
      <span className="text-4xl">{selectedAgent.avatar || '🤖'}</span>
    );

    return (
      <div className="fixed inset-0 flex flex-col bg-white">
        <AppBar
          onBack={handleCancelOnboarding}
          title={selectedAgent.nombreVisible}
          subtitle="Simulación · Vertex Quick"
        />

        {/* Contenido */}
        <div
          className="flex-1 overflow-hidden lg:bg-[#EEF2F7] lg:flex lg:items-start lg:justify-center lg:p-6"
          style={{ height: 'calc(100dvh - 52px)' }}
        >
          <div className="h-full w-full lg:max-w-4xl lg:rounded-2xl lg:overflow-hidden lg:shadow-[0_8px_40px_rgba(0,0,0,0.12)] lg:border lg:border-slate-200/70 flex flex-col lg:flex-row">

            {/* ── PANEL IZQUIERDO: Perfil del agente (desktop) ── */}
            <div className="hidden lg:flex lg:flex-col lg:w-2/5 bg-[#0c203b] shrink-0">
              {/* Avatar grande */}
              <div className="flex flex-col items-center text-center px-8 pt-12 pb-8">
                <div className="w-24 h-24 rounded-2xl overflow-hidden mb-4 flex items-center justify-center bg-slate-700"
                  style={{ boxShadow: '0 0 0 3px rgba(255,255,255,0.1), 0 8px 24px rgba(0,0,0,0.4)' }}>
                  {agentAvatarEl}
                </div>
                <h2 className="text-white font-bold text-xl leading-tight mb-1">
                  {selectedAgent.nombreVisible}
                </h2>

                <p className="text-slate-300/80 text-[13px] leading-relaxed italic">
                  "{selectedAgent.descripcion?.slice(0, 120)}{selectedAgent.descripcion?.length > 120 ? '...' : ''}"
                </p>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mx-6" />

              {/* Descripción del escenario */}
              {selectedAgent.scenarioTemplates?.[0]?.description && (
                <div className="px-8 pt-5 pb-2">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Descripción del caso</p>
                  <p className="text-slate-300 text-[12px] leading-relaxed">
                    {selectedAgent.scenarioTemplates[0].description}
                  </p>
                </div>
              )}

              {/* Stats del agente */}
              <div className="px-8 py-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/8 flex items-center justify-center shrink-0">
                    <Target className="w-4 h-4 text-slate-300" />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Tipo</p>
                    <p className="text-white text-[13px] font-medium">Simulación Conversacional</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/8 flex items-center justify-center shrink-0">
                    <MessageSquare className="w-4 h-4 text-slate-300" />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Duración</p>
                    <p className="text-white text-[13px] font-medium">
                      {selectedAgent.maxTurns ?? maxTurns} turnos · ~{Math.round((selectedAgent.maxTurns ?? maxTurns) * 2)} minutos
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/8 flex items-center justify-center shrink-0">
                    <Shield className="w-4 h-4 text-slate-300" />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Evaluación</p>
                    <p className="text-white text-[13px] font-medium">Reporte de desempeño IA</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ── PANEL DERECHO: Onboarding / Misión ── */}
            {/* min-h-0 es crítico para que overflow-y-auto funcione en flexbox */}
            <div className="flex-1 min-h-0 flex flex-col bg-white">

              {/* Mobile: avatar pequeño + nombre */}
              <div className="lg:hidden flex items-center gap-3 px-5 pt-5 pb-4 border-b border-slate-100 shrink-0">
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center shrink-0">
                  {agentAvatarEl}
                </div>
                <div>
                  <h2 className="font-bold text-[#0c203b] text-[15px] leading-tight">{selectedAgent.nombreVisible}</h2>
                  <p className="text-slate-400 text-[11px] mt-0.5">Simulación Conversacional · Motor V4</p>
                </div>
              </div>

              {/* Contenido scrollable */}
              <div className="flex-1 min-h-0 overflow-y-auto px-5 lg:px-8 py-5 lg:py-8 space-y-4 lg:space-y-5">

                {/* Etiqueta de fase */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">
                    Antes de comenzar
                  </span>
                  <div className="flex-1 h-px bg-slate-100" />
                </div>

                {/* Título */}
                <h3 className="text-2xl font-bold text-[#0c203b] leading-tight">
                  {onboardingData.title || 'Tu simulación está lista'}
                </h3>

                {/* Misión — bloque principal */}
                <div className="bg-[#EEF2F7] rounded-2xl p-5">
                  <p className="text-[10px] font-bold tracking-widest text-[#1E3A8A] uppercase mb-2 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1E3A8A]" />
                    Tu misión
                  </p>
                  <p className="text-[14px] text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {onboardingData.message || 'Pon a prueba tus habilidades ejecutivas con un cliente IA.'}
                  </p>
                </div>

                {/* Instrucciones rápidas */}
                <div className="space-y-2.5">
                  {[
                    'Escribe como si estuvieras en la situación real',
                    'Cada mensaje cuenta: el coach IA analizará tu desempeño',
                    'Al finalizar recibirás un reporte detallado'
                  ].map((tip, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#0c203b] flex items-center justify-center text-[10px] font-bold text-white shrink-0 mt-px">
                        {i + 1}
                      </div>
                      <p className="text-[13px] text-slate-600 leading-snug">{tip}</p>
                    </div>
                  ))}
                </div>

                {/* Consentimiento */}
                {onboardingData.consent && (
                  <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
                    <Shield className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-[12px] text-amber-800 leading-relaxed">
                      {onboardingData.consent}
                    </p>
                  </div>
                )}
              </div>

              {/* CTA fijo al pie */}
              <div
                className="px-5 lg:px-8 pt-4 border-t border-slate-100 shrink-0"
                style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
              >
                {/* Label de elección */}
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest text-center mb-3">
                  Elige cómo quieres practicar
                </p>

                {/* Botón principal: con voz */}
                <button
                  type="button"
                  style={{ touchAction: 'manipulation', boxShadow: '0 4px 20px rgba(30,58,138,0.30)' }}
                  onClick={async () => {
                    // El gesto de clic ES el unlock — no hay segundo modal
                    setIsTTSEnabled(true);
                    try { await unlockAudio(); } catch {}
                    handleConfirmOnboarding();
                  }}
                  className="w-full h-14 bg-[#1E3A8A] hover:bg-[#17316F] active:bg-[#0f2460] text-white font-bold text-[15px] rounded-2xl transition-colors flex items-center justify-center gap-3 mb-2.5"
                >
                  <Volume2 className="w-5 h-5" />
                  <span>Iniciar con voz</span>
                </button>

                {/* Botón secundario: solo texto */}
                <button
                  type="button"
                  onClick={() => {
                    setIsTTSEnabled(false);
                    handleConfirmOnboarding();
                  }}
                  className="w-full h-11 bg-slate-100 hover:bg-slate-200 active:bg-slate-200 text-slate-600 font-semibold text-[14px] rounded-2xl transition-colors flex items-center justify-center gap-2 mb-3"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Solo texto</span>
                </button>

                <button
                  onClick={handleCancelOnboarding}
                  className="w-full h-9 text-slate-400 text-[12px] font-medium hover:text-slate-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: SIMULATION — Chat de dos columnas
  // ══════════════════════════════════════════════════════════════════════════
  if ((phase === 'simulation' || phase === 'complete') && selectedAgent) {
    const isComplete = phase === 'complete';
    const progress = maxTurns > 0 ? Math.round((completedTurns / maxTurns) * 100) : 0;
    const agentAvatarSrc = selectedAgent.avatar?.startsWith('/') ? selectedAgent.avatar : null;
    const agentEmoji = !agentAvatarSrc ? (selectedAgent.avatar || '🤖') : null;

    const LeftPanelContent = () => (
      <div className="flex flex-col gap-5 px-5 py-5 overflow-y-auto flex-1">

        {/* Contexto del caso */}
        {briefingText && (
          <div>
            <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase mb-3">
              Contexto del caso
            </p>
            <ContextText text={briefingText} />
          </div>
        )}

        {/* Actor / Contraparte */}
        {briefingActor?.nombre && (
          <div className="border-t border-slate-200/60 pt-4">
            <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase mb-2">Contraparte</p>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#0c203b] flex items-center justify-center text-white text-xs font-bold shrink-0">
                {briefingActor.nombre.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-[13px] font-semibold text-slate-700">{briefingActor.nombre}</p>
                {(briefingActor.rol || briefingActor.organizacion) && (
                  <p className="text-[11px] text-slate-400">
                    {[briefingActor.rol, briefingActor.organizacion].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rol del usuario */}
        <div className="border-t border-slate-200/60 pt-4">
          <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase mb-1.5">Tu rol</p>
          <div className="flex items-center gap-2 text-[12px] text-slate-600">
            <Users className="w-3.5 h-3.5 text-slate-400" />
            {user?.nombre?.split(' ')[0] || 'Participante'}
          </div>
          {selectedAgent.labels?.participantRole && selectedAgent.labels.participantRole !== 'PARTICIPANTE' && (
            <p className="text-[11px] font-semibold text-[#1E3A8A] mt-1 uppercase tracking-wide">
              {selectedAgent.labels.participantRole}
            </p>
          )}
        </div>

        {/* Progreso */}
        {maxTurns > 0 && (
          <div className="border-t border-slate-200/60 pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase">Progreso</p>
              <span className="text-[11px] font-bold text-slate-600">{completedTurns}/{maxTurns}</span>
            </div>
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#1E3A8A] rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );

    return (
      <>
      <div className="fixed inset-0 flex flex-col bg-white">

        {/* App bar */}
        <AppBar
          onBack={() => {
            if (!isComplete && messages.length > 0) {
              setShowExitSheet(true);
            } else {
              handleExitSimulation();
            }
          }}
          title={selectedAgent.nombreVisible}
          subtitle={isComplete ? 'Simulación completada' : `Turno ${completedTurns}/${maxTurns}`}
          onMenu={() => setSidebarVisible(true)}
        />

        {/* ── Exit confirmation sheet ── */}
        {showExitSheet && (
          <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowExitSheet(false)} />
            <div
              className="fixed bottom-0 left-0 right-0 z-50 bg-white flex flex-col"
              style={{ borderRadius: '20px 20px 0 0', boxShadow: '0 -8px 32px rgba(0,0,0,0.2)', paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
            >
              <div className="flex justify-center pt-3 pb-1 shrink-0">
                <div className="w-10 h-1 rounded-full bg-slate-200" />
              </div>
              <div className="px-5 pt-4 pb-5">
                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </div>
                <h3 className="font-bold text-[#0c203b] text-[17px] text-center mb-1">¿Salir de la simulación?</h3>
                <p className="text-slate-500 text-[13px] text-center leading-relaxed mb-5">
                  Tu progreso se perderá y no se generará evaluación.
                </p>
                <button
                  onClick={() => { setShowExitSheet(false); handleExitSimulation(); }}
                  className="w-full h-12 bg-[#0c203b] hover:bg-[#081827] text-white font-bold text-[14px] rounded-2xl mb-2.5 transition-colors"
                >
                  Sí, salir
                </button>
                <button
                  onClick={() => setShowExitSheet(false)}
                  className="w-full h-12 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold text-[14px] rounded-2xl transition-colors"
                >
                  Continuar simulación
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Bottom sheet contexto — MOBILE ── */}
        {sheetOpen && (
          <>
            <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSheetOpen(false)} />
            <div
              className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white flex flex-col"
              style={{ height: '80dvh', borderRadius: '20px 20px 0 0', boxShadow: '0 -8px 32px rgba(0,0,0,0.18)' }}
            >
              <div className="flex justify-center pt-3 pb-1 shrink-0">
                <div className="w-10 h-1 rounded-full bg-slate-200" />
              </div>
              {/* Header del sheet */}
              <div className="mx-4 mt-2 mb-3 rounded-2xl flex items-center gap-3 px-4 py-3 shrink-0 bg-[#0c203b]">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-700 flex items-center justify-center shrink-0"
                  style={{ boxShadow: '0 0 0 2px rgba(255,255,255,0.15)' }}>
                  {agentAvatarSrc
                    ? <img src={agentAvatarSrc} alt={selectedAgent.nombreVisible} className="w-full h-full object-cover object-top" />
                    : <span className="text-xl">{agentEmoji}</span>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-[13px] leading-tight">{selectedAgent.nombreVisible}</p>
                  <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase">Coach IA</p>
                </div>
                <p className="text-slate-400 text-[11px] italic leading-snug max-w-[120px] text-right">
                  "Revisa antes de responder."
                </p>
              </div>
              <div className="flex-1 overflow-y-auto">
                <LeftPanelContent />
              </div>
              <div className="px-4 py-4 border-t border-slate-100 shrink-0"
                style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
                <button
                  onClick={() => { setSheetOpen(false); setTimeout(() => inputRef.current?.focus(), 200); }}
                  className="w-full h-12 bg-[#1E3A8A] hover:bg-[#17316F] text-white font-semibold text-[14px] rounded-2xl transition-colors flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Responder ahora
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── Layout principal de dos columnas ── */}
        <div
          className="overflow-hidden lg:bg-[#EEF2F7] lg:flex lg:items-start lg:justify-center lg:p-6"
          style={{ height: 'calc(100dvh - 52px)' }}
        >
          <div className="h-full w-full lg:max-w-5xl lg:rounded-xl lg:overflow-hidden lg:shadow-[0_4px_24px_rgba(0,0,0,0.10)] lg:border lg:border-slate-200/70 flex flex-col lg:flex-row">

            {/* ── COLUMNA IZQUIERDA — Contexto (35%) — solo desktop ── */}
            <div className="hidden lg:flex lg:flex-col lg:w-[35%] border-r border-[#E5E7EB] bg-[#F1F5F9] border-l-[3px] border-l-[#0c203b] overflow-hidden">

              {/* Cabecera del agente */}
              <div className="bg-[#0c203b] px-5 pt-7 pb-6 shrink-0 flex flex-col items-center text-center">
                <div
                  className="w-[76px] h-[76px] rounded-full overflow-hidden mb-3 bg-slate-700 flex items-center justify-center"
                  style={{ boxShadow: '0 0 0 2px rgba(255,255,255,0.13), 0 0 0 5px rgba(255,255,255,0.05), 0 8px 20px rgba(0,0,0,0.45)' }}
                >
                  {agentAvatarSrc
                    ? <img src={agentAvatarSrc} alt={selectedAgent.nombreVisible} className="w-full h-full object-cover object-top" />
                    : <span className="text-3xl">{agentEmoji}</span>
                  }
                </div>
                <p className="text-white font-semibold text-[15px] leading-tight tracking-tight">
                  {selectedAgent.nombreVisible}
                </p>
                <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase mt-1">
                  Agente IA · Vertex Quick
                </p>
                <p className="text-slate-400 text-[11px] leading-[1.65] mt-3 italic px-3">
                  {briefingActor?.rol
                    ? `"${briefingActor.nombre} · ${briefingActor.rol}"`
                    : '"Revisa el contexto antes de responder."'
                  }
                </p>
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-slate-300/50 to-transparent shrink-0" />

              <LeftPanelContent />
            </div>

            {/* ── COLUMNA DERECHA — Chat (65%) ── */}
            <div className="flex-1 w-full flex flex-col bg-white min-h-0">

              {/* Header del chat */}
              <div
                className="px-3 lg:px-5 border-b border-[#E5E7EB] flex items-center justify-between flex-shrink-0"
                style={{ height: '52px' }}
              >
                <div className="flex items-center gap-2">
                  {!isComplete && (
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                  )}
                  {isComplete && (
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                  )}
                  <span className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider">
                    {isComplete ? 'Simulación finalizada' : (briefingActor?.nombre || selectedAgent.nombreVisible)}
                  </span>
                </div>
                {/* "Ver contexto" — solo mobile, visible después de ver contexto */}
                {contextViewed && !isComplete && (
                  <button
                    className="lg:hidden flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full"
                    onClick={() => setSheetOpen(true)}
                  >
                    Escenario <ChevronDown className="w-3 h-3" />
                  </button>
                )}
              </div>

              {/* Messages */}
              <div
                ref={chatBodyRef}
                className="flex-1 overflow-y-auto px-3 lg:px-4 py-4 space-y-3 lg:space-y-4"
                style={{ WebkitOverflowScrolling: 'touch', paddingBottom: '16px' }}
              >
                {/* Estado vacío — paso 1 mobile */}
                {messages.length === 0 && !isComplete && (
                  <div className="flex flex-col items-center px-4 mt-4">
                    <div className="lg:hidden w-full max-w-sm">
                      {!contextViewed ? (
                        <button
                          className="w-full text-left rounded-2xl px-5 py-5 transition-opacity active:opacity-90"
                          style={{ background: '#0c203b', boxShadow: '0 4px 24px rgba(8,24,39,0.30)' }}
                          onClick={() => { setSheetOpen(true); setContextViewed(true); }}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-700 shrink-0"
                              style={{ boxShadow: '0 0 0 2px rgba(255,255,255,0.18)' }}>
                              {agentAvatarSrc
                                ? <img src={agentAvatarSrc} alt="" className="w-full h-full object-cover object-top" />
                                : <div className="w-full h-full flex items-center justify-center">{agentEmoji}</div>
                              }
                            </div>
                            <div>
                              <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">Antes de responder</p>
                              <p className="text-white font-bold text-[15px] leading-tight">Lee el contexto del caso</p>
                            </div>
                          </div>
                          <div className="w-full h-11 rounded-xl flex items-center justify-center gap-2 font-semibold text-[13px] text-white"
                            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}>
                            Ver escenario
                            <ArrowRight className="w-4 h-4" />
                          </div>
                        </button>
                      ) : (
                        <div className="w-full flex items-center gap-3 rounded-xl px-4 py-3 border border-emerald-200 bg-emerald-50">
                          <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                          </div>
                          <div>
                            <p className="text-[13px] font-semibold text-emerald-700">Contexto revisado</p>
                            <p className="text-[11px] text-emerald-600/70 mt-px">Ya puedes escribir tu respuesta</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Mensajes */}
                {messages.map(msg => {
                  if (msg.role === 'agent') {
                    return (
                      <div key={msg.id} className="flex items-end gap-2.5">
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-200 flex items-center justify-center shrink-0 border border-slate-200 shadow-sm">
                          {agentAvatarSrc
                            ? <img src={agentAvatarSrc} alt="" className="w-full h-full object-cover object-top" />
                            : <span className="text-sm">{agentEmoji}</span>
                          }
                        </div>
                        <div className="flex flex-col items-start min-w-0 max-w-[85%]">
                          <span className="text-[10px] text-slate-400 mb-1 ml-1">
                            {briefingActor?.nombre || selectedAgent.nombreVisible}
                          </span>
                          <div className="px-4 py-3 text-[14px] leading-relaxed rounded-2xl rounded-bl-sm bg-white text-slate-700 border border-[#E5E7EB] shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
                            {msg.content}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  if (msg.role === 'user') {
                    return (
                      <div key={msg.id} className="flex flex-col items-end">
                        <span className="text-[10px] text-slate-400 mb-1 mr-0.5">Tú</span>
                        <div className="max-w-[85%] px-4 py-3 text-[14px] leading-relaxed rounded-2xl rounded-br-sm bg-[#1E3A8A] text-white">
                          {msg.content}
                        </div>
                      </div>
                    );
                  }
                  if (msg.role === 'system') {
                    return (
                      <div key={msg.id} className="flex justify-center">
                        <div className="bg-red-50 text-red-500 text-[11px] border border-red-100 rounded-lg px-3 py-1.5">
                          {msg.content}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}

                {/* Typing indicator */}
                {sending && (
                  <div className="flex items-end gap-2.5">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-200 flex items-center justify-center shrink-0 border border-slate-200 shadow-sm">
                      {agentAvatarSrc
                        ? <img src={agentAvatarSrc} alt="" className="w-full h-full object-cover object-top" />
                        : <span className="text-sm">{agentEmoji}</span>
                      }
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-[10px] text-slate-400 mb-1 ml-1">
                        {briefingActor?.nombre || selectedAgent.nombreVisible}
                      </span>
                      <div className="bg-white border border-[#E5E7EB] shadow-[0_1px_3px_rgba(0,0,0,0.06)] px-4 py-3 rounded-2xl rounded-bl-sm text-slate-400 text-[14px]">
                        <span className="animate-pulse">···</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>

              {/* ── PANTALLA DE FIN (session complete) ── */}
              {isComplete && (
                <div
                  className="border-t border-slate-100 px-4 py-5 flex flex-col items-center text-center gap-4 bg-gradient-to-t from-slate-50 to-white shrink-0"
                  style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
                >
                  <div className="w-12 h-12 rounded-full bg-[#0c203b] flex items-center justify-center"
                    style={{ boxShadow: '0 4px 16px rgba(8,24,39,0.3)' }}>
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0c203b] text-[16px] leading-tight mb-1">
                      Simulación completada
                    </h3>
                    <p className="text-slate-500 text-[12px] leading-relaxed max-w-xs mx-auto">
                      Has completado {completedTurns} turnos. Genera tu reporte de desempeño.
                    </p>
                  </div>
                  <button
                    onClick={handleGenerateEvaluation}
                    disabled={isGeneratingEval}
                    className="w-full max-w-xs h-13 bg-[#1E3A8A] hover:bg-[#17316F] disabled:opacity-60 text-white font-bold text-[14px] rounded-2xl transition-colors flex items-center justify-center gap-2 py-3"
                    style={{ boxShadow: '0 4px 16px rgba(30,58,138,0.35)' }}
                  >
                    {isGeneratingEval ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Generando reporte...</>
                    ) : (
                      <><BarChart3 className="w-4 h-4" /> Ver evaluación IA</>
                    )}
                  </button>
                  <button
                    onClick={handleExitSimulation}
                    className="text-slate-400 text-[12px] hover:text-slate-600 transition-colors"
                  >
                    Volver al inicio
                  </button>
                </div>
              )}

              {/* ── ÁREA DE INPUT ── */}
              {!isComplete && (
                <div
                  className="flex-shrink-0"
                  style={{
                    background: 'linear-gradient(to bottom, #f1f5f9 0%, #ffffff 18px)',
                    borderTop: '1px solid rgba(30,58,138,0.10)',
                    boxShadow: '0 -4px 16px 0 rgba(30,58,138,0.06)',
                  }}
                >
                  {/* Alerta de moderación */}
                  {moderationAlert && (
                    <div className="flex items-start gap-2 px-4 pt-3 pb-2 text-[12px] text-amber-700 bg-amber-50 border-b border-amber-100">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span className="leading-snug">{moderationAlert}</span>
                    </div>
                  )}

                  {/* VoiceInputUX */}
                  <div
                    className="px-3 pt-3"
                    style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
                    onFocus={handleInputFocus}
                  >
                    <VoiceInputUX
                      inputValue={input}
                      onInputChange={v => setInput(sanitizeInput(v))}
                      onSend={sendMessage}
                      onVoiceSend={async (text) => {
                        const clean = sanitizeInput(text).trim();
                        if (!clean || clean.length < 2 || sending || phase !== 'simulation') return;
                        const uid = msgId();
                        setMessages(prev => [...prev, { id: uid, role: 'user', content: clean }]);
                        setSending(true);
                        const token = getSafeToken();
                        try {
                          const res = await fetch(`${VERTEX_API_BASE}/v4/session/turn`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify({ sessionId, message: clean })
                          });
                          const data = await res.json();
                          if (data.sessionClosed || data.status === 'armed_to_close') { setPhase('complete'); return; }
                          const aiText = data.content || data.response || data.messages?.[0]?.content || '';
                          if (aiText.trim()) {
                            const aid = msgId();
                            setMessages(prev => [...prev, { id: aid, role: 'agent', content: aiText.trim() }]);
                            generateAndPlayAudio(aiText.trim(), aid);
                          }
                          const newTurns = data.counters?.completedTurns ?? data.completedTurns ?? completedTurns + 1;
                          setCompletedTurns(newTurns);
                          if (data.isComplete || data.sessionStatus === 'completed' || newTurns >= maxTurns) setPhase('complete');
                        } catch {
                          setMessages(prev => [...prev, { id: msgId(), role: 'system', content: 'Error al procesar. Intenta de nuevo.' }]);
                        } finally { setSending(false); }
                      }}
                      disabled={sending}
                      placeholder="Escribe tu respuesta…"
                      isSending={sending}
                      assistantAudioState={assistantAudioState}
                      assistantState={sending ? 'thinking' : 'idle'}
                      onStopAudio={() => {
                        if (audioEngineRef.current) {
                          try { audioEngineRef.current.pause(); } catch {}
                        }
                        setAssistantAudioState('idle');
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Nav sidebar — fixed wrapper keeps it out of flex layout */}
      <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 60, display: sidebarVisible ? 'block' : 'none' }}>
        <SidebarAsistentes
          userName={user?.nombre || user?.name || ''}
          userEmail={user?.email || ''}
          allUserChats={{}}
          handleSelectAssistantForNewChat={() => {}}
          handleSelectChat={() => {}}
          handleDeleteChat={() => {}}
          handleRenameChat={() => {}}
          selectedAssistant={null}
          selectedChatId={null}
          expanded={false}
          setExpanded={() => {}}
          sidebarVisible={sidebarVisible}
          setSidebarVisible={setSidebarVisible}
          isCreatingNewChat={false}
          MAX_CHATS_PER_ASSISTANT={10}
          loading={false}
          isAuthenticated={true}
          onBackToDashboard={() => navigate('/dashboard')}
        />
      </div>
      {sidebarVisible && (
        <div
          className="fixed inset-0 bg-black/40 z-[59] cursor-pointer"
          onClick={() => setSidebarVisible(false)}
        />
      )}
      </>
    );
  }

  // Fallback
  return null;
}
